import * as yup from 'yup';

import { ReviewType, Utils } from '@mvrb-frontend/shared-ui';

import moment from 'moment';
import { ReviewStatusEnum, CommentStatusEnum, UserProfileMode } from './arrays';
import { Provinces, CommentTypeEnum, Prefixes, CUSTOM_TOPIC } from '../constants';

const PASSWORD_PATTERN = /(?=.*\d)(?=.*[A-Z])(?=.*[a-z])(?!.*[^a-zA-Z0-9-!@#$%^&*()\-_+|~={}[]:";'<>?,.\/]).*/;
const POSTAL_CODE_PATTERN = /^(?:[A-Z]\d){3}$/;
const URL_PATTERN = /^(http[s]?:\/\/)?([a-zA-Z0-9\.-]+)\.[a-zA-Z]+/;
const URL_PATTERN_WITH_PROTOCOL = /^(http[s]?:\/\/)([a-zA-Z0-9\.-]+)\.[a-zA-Z]+/;
const NUMBER_REGEX = /^[0-9]+$/;
const HEX_COLOR = /#([A-Fa-f0-9]{6})$/;

const richTextContentValidation = config => {
  return yup
    .mixed()
    .required(config.EMPTY_RICH_TEXT_ERROR_MESSAGE)
    .transform(richTextInput => {
      return Utils.richTextContentToPlainText(richTextInput);
    })
    .test('transformation', config.TRANSFORMATION_ERROR_MESSAGE, value => {
      return !(value instanceof Error);
    })
    .test('string type', config.TYPE_ERROR_MESSAGE, value => {
      return typeof value === 'string' || value instanceof String;
    })
    .test('non-empty richText content', config.EMPTY_RICH_TEXT_CONTENT_ERROR_MESSAGE, value => {
      return Boolean(value);
    })
    .test('max length', `Must be ${config.max - 1} characters or less`, value => {
      return value?.length < config.max;
    });
};

const optionalRichTextContentValidation = config => {
  return yup
    .mixed()
    .transform(richTextInput => {
      return Utils.richTextContentToPlainText(richTextInput);
    })
    .test('transformation', config.TRANSFORMATION_ERROR_MESSAGE, value => {
      return !(value instanceof Error);
    })
    .test('string type', config.TYPE_ERROR_MESSAGE, value => {
      return typeof value === 'string' || value instanceof String;
    })
    .test('max length', `Must be ${config.max - 1} characters or less`, value => {
      return value?.length < config.max;
    });
};

export const CreateReviewSchema = yup.object().shape({
  boardId: yup.string().required('Admin Organization is required'),
  name: yup.string().required('Project Name is required').max(255, 'Maximum 255 characters'),
  title: yup.string().max(255, 'Maximum 255 characters').required('This field is required') /* nullable(), */,
  organizationId: yup.string().required('This field is required'),
  commentDueAt: yup.string().when('responseDueAt', (responseDueAt, schema) => {
    return schema.test(
      'commentDueAt',
      'Review Comments Due date cannot be in future to Proponent Responses Due date',
      value => {
        if (responseDueAt && moment(responseDueAt).diff(value, 'days') < 0) {
          return false;
        }
        return true;
      },
    );
  }),
  phase: yup
    .string()
    .nullable()
    .when('type', {
      is: ReviewType.InformationRequest,
      then: yup
        .string()
        .optional()
        .matches(NUMBER_REGEX, { message: 'Invalid phase number', excludeEmptyString: true }),
    }),
  registryNumber: yup
    .string()
    .nullable()
    .when('type', {
      is: ReviewType.InformationRequest,
      then: yup
        .string()
        .optional()
        .matches(NUMBER_REGEX, { message: 'Invalid registry number', excludeEmptyString: true }),
    }),
});

export const PublishReviewSchema = yup.object().shape(
  {
    // required fileds
    status: yup.string().required('Status is required').oneOf(Object.values(ReviewStatusEnum)),
    boardId: yup.string().required('Admin Organization is required'),
    name: yup.string().required('Project Name is required').max(255, 'Maximum 255 characters'),
    title: yup.string().required('Title is required').max(255, 'Maximum 255 characters'),

    // optional
    publicReviews: yup.array(),

    // required only when publishing a draft
    organizationId: yup.string().required('Proponent is required'),
    timelineStartDate: yup.string().when('type', {
      is: ReviewType.Review,
      then: yup.string().required('Submission Complete/Conforms is required').nullable(),
    }),
    submissionReceivedDate: yup.string().when('type', {
      is: ReviewType.Review,
      then: yup
        .string()
        .required('Submission Received Date is required')
        .nullable()
        .test(
          'submissionReceivedDateBeforeCompleted',
          'Submission Received Date must be before or equal to Submission Complete/Conforms',
          function (value) {
            return (
              !value ||
              !this.parent.timelineStartDate ||
              moment(value).isSameOrBefore(moment(this.parent.timelineStartDate), 'day')
            );
          },
        ),
    }),
    fileNumber: yup.string().required('File Number is required').max(255, 'Maximum 255 characters'),
    registryUrl: yup
      .string()
      .required('File URL is required')
      .max(500, 'Maximum 500 characters')
      .matches(URL_PATTERN_WITH_PROTOCOL, 'Must be a valid URL with http:// or https:// in the beginning'),
    permit: yup.array().required('At least one option is required'),
    activityId: yup.string().required('Activity is required'),
    reviewParadigm: yup.array().required('At least one option is required'),
    responseDueAt: yup.string().required('Proponent Responses Due is required').nullable(),
    completedAt: yup
      .string()
      .nullable()
      .when('responseDueAt', (responseDueAt, schema) => {
        return schema.test('completedAt', 'Decision date must be on or after Proponent Responses Due', value => {
          if (responseDueAt && value) {
            return moment(value).isSameOrAfter(moment(responseDueAt));
          }
          return true;
        });
      })
      .when('status', (status, schema) => {
        return schema.test('completedAt', 'Decision date is required for completed reviews', value => {
          if (status === ReviewStatusEnum.COMPLETED) {
            return !!value;
          }

          return true;
        });
      })
      .when('status', (status, schema) => {
        return schema.test('completedAt', 'Decision date is not a valid date', value => {
          if (status === ReviewStatusEnum.COMPLETED) {
            return moment(value).isValid();
          }

          return true;
        });
      }),
    commentDueAt: yup
      .string()
      .required('Review Comments Due is required')
      .nullable()
      .when('responseDueAt', (responseDueAt, schema) => {
        return schema.test(
          'commentDueAt',
          'Review Comments Due date cannot be in future to Proponent Responses Due date',
          value => {
            if (responseDueAt && moment(responseDueAt).diff(value, 'days') < 0) {
              return false;
            }
            return true;
          },
        );
      }),
    description: richTextContentValidation({
      max: 30001,
      EMPTY_RICH_TEXT_ERROR_MESSAGE: 'Description is required',
      TRANSFORMATION_ERROR_MESSAGE: 'Rich Text parsing error',
      TYPE_ERROR_MESSAGE: 'Description should be a string',
      EMPTY_RICH_TEXT_CONTENT_ERROR_MESSAGE: 'Description is required',
    }),
    proponentUserIds: yup.array().when('type', {
      is: ReviewType.Review,
      then: yup.array().required('At least one proponent user is required'),
    }),
    deletedStaffUsers: yup.array(),
    staffMemberIds: yup
      .array()
      .required('At least one staff contact is required')
      .test({
        name: 'staffMemberDeleted',
        test: function (value: any) {
          const deletedUsers = this.parent.deletedStaffUsers || [];

          const deletedUser = deletedUsers.find(u => (value || []).includes(u.id));

          if (deletedUser) {
            return this.createError({
              message: `${deletedUser.firstName} ${deletedUser.lastName} is a deleted user`,
              path: 'staffMemberIds',
            });
          }

          return true;
        },
      }),
    primaryContactId: yup.string().required('Primary Proponent Contact is required'),
    documents: yup.array().of(
      yup.object().shape({
        documentURL: yup.string().url('Document URL is invalid'),
        name: yup.string().max(512, 'Must be 512 characters or less'),
      }),
    ),
    location: yup
      .object()
      .shape({
        latlng: yup.string().test('latlng', 'Must enter proper latitude and longitude', value => {
          return Utils.validateStrCoordinates(value || '');
        }),
      })
      .required('Requires a latitude and longitude'),
    distributionListIds: yup
      .array()
      .when('distributionUserIds', (distributionUserIds, schema) =>
        schema.test(
          'distributionListIds',
          'Please select at least one distribution list or user',
          value => value.length > 0 || distributionUserIds.length > 0,
        ),
      ),
    distributionUserIds: yup
      .array()
      .when('distributionListIds', (distributionListIds, schema) =>
        schema.test(
          'distributionUserIds',
          'Please select at least one distribution list or user',
          value => value.length > 0 || distributionListIds.length > 0,
        ),
      ),
    phase: yup
      .string()
      .nullable()
      .when('type', {
        is: ReviewType.InformationRequest,
        then: yup.string().matches(NUMBER_REGEX, { message: 'Invalid phase number', excludeEmptyString: true }),
      }),
    registryNumber: yup
      .string()
      .nullable()
      .when('type', {
        is: ReviewType.InformationRequest,
        then: yup.string().matches(NUMBER_REGEX, { message: 'Invalid registry number', excludeEmptyString: true }),
      }),
    additionalFileNumbers: yup.array().of(
      yup.object().shape({
        fileNumber: yup.string().required('File Number is required').max(255, 'Maximum 255 characters'),
        registryUrl: yup
          .string()
          .required('File URL is required')
          .max(500, 'Maximum 500 characters')
          .matches(URL_PATTERN_WITH_PROTOCOL, 'Must be a valid URL with http:// or https:// in the beginning'),
      }),
    ),
  },
  [['distributionUserIds', 'distributionListIds']],
);

export const LoginSchema = yup.object().shape({
  email: yup.string().email('Must be an email address').required('Email is required'),
  password: yup.string().required('Password is required'),
});

export const AdminOrgSchema = yup.object().shape({
  name: yup.string().required('Display Name is required').max(255, 'Must be less than 255 characters').ensure(),
  abbreviation: yup.string().required('Abbreviation is required'),
  hyperLinkTitle: yup
    .string()
    .required('Hyperlink Title is required')
    .max(255, 'Must be less than 255 characters')
    .ensure(),
  url: yup
    .string()
    .required('URL is required')
    .max(255, 'Must be less than 255 characters')
    .ensure()
    .matches(URL_PATTERN, `Must be a valid URL e.g https://reviewboard.ca/MVEIRB_RulesofProcedure_0505.pdf`),
  procedureRules: yup
    .string()
    .required('Procedure Text are required')
    .max(255, 'Must be less than 255 characters')
    .ensure(),
  file: yup.bool().oneOf([true], 'A file to upload is required'),
  hexColor: yup
    .string()
    .required('Hex Color Code is required')
    .max(7, 'Must be less than 7 characters')
    .ensure()
    .matches(
      HEX_COLOR,
      'The code should start with # and contain a 6 digit combination of the sixteen characters 0-9 and A-F, e.g. #FF0000',
    ),
});

export const OrganizationSchema = yup.object().shape({
  name: yup.string().required('Name is required').max(255, 'Must be less than 255 characters').ensure(),
  address: yup.string().required('Address is required').max(255, 'Must be less than 255 characters'),
  city: yup.string().required('City is required').max(35, 'Must be less than 35 characters'),
  province: yup
    .string()
    .required('Province/territory is required')
    .oneOf(
      Provinces.map(x => x.label),
      'Select one of the provided values',
    ),
  postalCode: yup
    .string()
    .required('Postal code is required')
    .matches(POSTAL_CODE_PATTERN, { message: 'Must match the following format: A1A1A1', excludeEmptyString: true }),
  telephoneNumber: yup.string().max(32, 'Must must be less than 32 characters').optional(),
  faxNumber: yup.string().max(32, 'Must must be less than 32 characters').optional(),
  webpage: yup
    .string()
    .matches(URL_PATTERN, { message: 'Must be a valid domain name', excludeEmptyString: true })
    .optional(),
});

export const EngagementSchema = yup.object().shape({
  name: yup.string().required('Name is required').max(255, 'Must be less than 255 characters').ensure(),
  itemUrl: yup
    .string()
    .matches(URL_PATTERN, {
      message: `Must be a valid URL e.g https://reviewboard.ca/MVEIRB_RulesofProcedure_0505.pdf`,
      excludeEmptyString: true,
    })
    .max(500, 'Must be less than 500 characters')
    .nullable(true),
  location: yup
    .array()
    .required('Location is required')
    .of(
      yup.object().shape({
        label: yup.string(),
        value: yup.string(),
      }),
    ),
  engagementItems: yup.array().of(
    yup.object().shape({
      itemTitle: yup
        .string()
        .required('Contact Information is Required')
        .max(255, 'Must be less than 255 characters')
        .ensure(),
    }),
  ),
});

export const SetPasswordSchema = yup.object().shape({
  password: yup
    .string()
    .required('Password is required')
    .min(6, 'Password must be more than 5 characters')
    .max(30, 'Password must be less than 30 characters')
    .matches(PASSWORD_PATTERN, 'Password must contain at least 1 uppercase letter, 1 lowercase letter, and 1 digit'),
});

export const RegistrationSchema = yup.object().shape({
  firstName: yup.string().required('First name is required').max(100, 'First name should be less that 100 characters'),
  lastName: yup.string().required('Last name is required').max(100, 'Last name should be less that 100 characters'),
  prefix: yup
    .string()
    .optional()
    .oneOf(Prefixes.map(x => x.label)),
  title: yup.string().required('Title is required').max(50, 'Title should be less that 50 characters'),
  email: yup
    .string()
    .email('Must be an email address')
    .required('Email is required')
    .max(255, 'Email should be less that 255 characters'),
  confirmEmail: yup
    .string()
    .required('Please confirm your email')
    .oneOf([yup.ref('email')], 'Emails must match'),
  password: yup
    .string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters or more')
    .max(30, 'Password must be less than 30 characters')
    .matches(PASSWORD_PATTERN, 'Password must contain at least 1 uppercase letter, 1 lowercase letter, and 1 digit'),
  confirmPassword: yup
    .string()
    .required('Please confirm your password')
    .oneOf([yup.ref('password')], 'Passwords must match'),
  organizationIds: yup.array().when('isNewOrganization', {
    is: isNewOrganization => !isNewOrganization,
    then: yup.array().required('Please select or add at least one organization').min(1),
  }),
  community: yup.string().optional(),
  contact: yup.object().shape({
    city: yup.string().optional().max(35, 'City should be less that 35 characters'),
    province: yup
      .string()
      .optional()
      .oneOf(Provinces.map(x => x.label)),
    postalCode: yup
      .string()
      .optional()
      .matches(POSTAL_CODE_PATTERN, { excludeEmptyString: true, message: 'Must match the following format: A1A1A1' })
      .max(6, 'Postal Code should be 6 characters'),
    country: yup.string().optional(),
    officePhone: yup.string().required('Office Phone is required').max(32, 'Must must be less than 32 characters'),
    cellPhone: yup.string().max(32, 'Must must be less than 32 characters').optional(),
    faxNumber: yup.string().max(32, 'Must must be less than 32 characters').optional(),
  }),
  newOrganization: OrganizationSchema.optional()
    .default(undefined)
    .when('isNewOrganization', {
      is: isNewOrganization => isNewOrganization,
      then: OrganizationSchema.required(),
    })
    .when('isNewOrganization', {
      is: isNewOrganization => !isNewOrganization,
      then: OrganizationSchema.optional().default(undefined),
    }),
});

export const ResetPasswordSchema = yup.object().shape({
  email: yup.string().required('Email is required'),
});

export const UploadCommentFileSchema = yup.object().shape({
  commentFile: yup.object().required('You need to upload an excel file'),
});

export const UserInviteSchema = yup.object().shape({
  existingUsers: yup.array().of(yup.string()).optional(),
  inviteUsers: yup
    .array()
    .of(
      yup.object().shape({
        firstName: yup
          .string()
          .required('First name is required')
          .max(100, 'First name should be less that 100 characters'),
        lastName: yup
          .string()
          .required('Last name is required')
          .max(100, 'Last name should be less that 100 characters'),
        email: yup
          .string()
          .email('Must be an email address')
          .required('Email is required')
          .max(255, 'Email should be less that 255 characters'),
      }),
    )
    .optional(),
});

export const EditHistorySchema = yup.object().shape({
  editList1: yup.array().of(
    yup.object().shape({
      header: yup.string().required('Select a header'),
      description: yup.string().required('Description is required'),
      id: yup.number().required(),
      idDes: yup.number().required(),
    }),
  ),
});

export const AddRemoveListUsersSchema = yup.object().shape({
  newAddedUsers: yup.array().of(
    yup.object().shape({
      reason: yup.string().required('Description is required'),
    }),
  ),
  removedUsers: yup.array().of(
    yup.object().shape({
      reason: yup.string().required('Description is required'),
    }),
  ),
  newDLAdded: yup.array().nullable(),
  newDLAddedReason: yup
    .string()
    .nullable()
    .test('newDLAddedReason-test', 'Description is required', function (value) {
      const { newDLAdded } = this.parent;
      if (newDLAdded && newDLAdded.length > 0) {
        return value !== undefined && value !== '';
      }
      return true;
    }),
});

export const AddCommentsSchema = yup.object().shape({
  comments: yup.array().of(
    yup.object().shape({
      id: yup.string().optional(),
      topicId: yup.string().optional(),
      customTopic: yup
        .mixed()
        .nullable()
        .when('topicId', {
          is: CUSTOM_TOPIC,
          then: optionalRichTextContentValidation({
            max: 256,
            EMPTY_RICH_TEXT_ERROR_MESSAGE: 'A topic is required',
            TRANSFORMATION_ERROR_MESSAGE: 'Rich Text parsing error',
            TYPE_ERROR_MESSAGE: 'Custom topic should be a string',
            EMPTY_RICH_TEXT_CONTENT_ERROR_MESSAGE: 'A topic is required',
          }),
        }),
      description: yup.mixed().when('type', {
        is: type => type != CommentTypeEnum.Audio,
        then: richTextContentValidation({
          max: 30001,
          EMPTY_RICH_TEXT_ERROR_MESSAGE: 'Description is required',
          TRANSFORMATION_ERROR_MESSAGE: 'Rich Text parsing error',
          TYPE_ERROR_MESSAGE: 'Description should be a string',
          EMPTY_RICH_TEXT_CONTENT_ERROR_MESSAGE: 'Description is required',
        }),
      }),
      recommendation: optionalRichTextContentValidation({
        max: 30001,
        TRANSFORMATION_ERROR_MESSAGE: 'Rich Text parsing error',
        TYPE_ERROR_MESSAGE: 'Recommendation should be a string',
      }),
      attachments: yup.array().of(
        yup.object().shape({
          id: yup.string(),
        }),
      ),
      status: yup.string().required('Status is required').default(CommentStatusEnum.DRAFT),
    }),
  ),
  type: yup.string(),
  reviewId: yup.string().required('Review id is required'),
  organizationId: yup.string().required('Organization id is required'),
  authorId: yup.string().required('Author id is required'),
});

export const AddInformationRequestSchema = yup.object().shape({
  comments: yup
    .array()
    .required('You must add at least one request')
    .of(
      yup.object().shape({
        id: yup.string().optional(),
        topicId: yup.string().optional(),
        customTopic: yup.string().nullable().when('topicId', {
          is: CUSTOM_TOPIC,
          then: yup.string(),
        }),
        addressToId: yup.string().required('AddressTo is required').nullable(),
        description: yup.mixed().when('type', {
          is: type => type != CommentTypeEnum.Audio,
          then: richTextContentValidation({
            max: 30001,
            EMPTY_RICH_TEXT_ERROR_MESSAGE: 'Preamble is required',
            TRANSFORMATION_ERROR_MESSAGE: 'Rich Text parsing error',
            TYPE_ERROR_MESSAGE: 'Preamble should be a string',
            EMPTY_RICH_TEXT_CONTENT_ERROR_MESSAGE: 'Preamble is required',
          }),
        }),
        recommendation: optionalRichTextContentValidation({
          max: 30001,
          TRANSFORMATION_ERROR_MESSAGE: 'Rich Text parsing error',
          TYPE_ERROR_MESSAGE: 'Request should be a string',
        }),
        attachments: yup.array().of(
          yup.object().shape({
            id: yup.string(),
          }),
        ),
        status: yup.string().required('Status is required').default(CommentStatusEnum.DRAFT),
      }),
    ),
  type: yup.string(),
  reviewId: yup.string().required('Review id is required'),
  organizationId: yup.string().required('Organization id is required'),
  authorId: yup.string().required('Author id is required'),
});

export const AddResponsesSchema = yup.object().shape({
  comments: yup.array().of(
    yup.object().shape({
      responseDescription: optionalRichTextContentValidation({
        max: 30001,
        TRANSFORMATION_ERROR_MESSAGE: 'Rich Text parsing error',
        TYPE_ERROR_MESSAGE: 'Response should be a string',
      }),
    }),
  ),
});

export const AddCommentsReviewerSchema = yup.object().shape({
  organizationId: yup.string().required('Organization is required').nullable(),
  reviewerId: yup.string().required('Reviewer is required').nullable(),
});

export const AddResponseProponentSchema = yup.object().shape({
  proponentId: yup.string().required('Proponent is required').nullable(),
});

export const EditUserProfileSchema = (mode: UserProfileMode) =>
  yup.object().shape({
    firstName: yup
      .string()
      .required('First name is required')
      .max(100, 'First name should be less that 100 characters'),
    lastName: yup.string().required('Last name is required').max(100, 'Last name should be less that 100 characters'),
    prefix: yup
      .string()
      .optional()
      .oneOf(Prefixes.map(x => x.label)),
    title: yup.string().required('Title is required').max(50, 'Title should be less that 50 characters'),
    organizationIds: yup.array().required('Please select at least one organization').min(1),
    community: yup.string().optional(),
    ...(mode === UserProfileMode.AddByStaff && {
      email: yup
        .string()
        .email('Must be an email address')
        .required('Email is required')
        .max(255, 'Email should be less that 255 characters'),
      confirmEmail: yup
        .string()
        .required('Please confirm your email')
        .oneOf([yup.ref('email')], 'The email addresses do not match. Please re-enter the correct email address'),
    }),
    contact: yup.object().shape({
      city: yup.string().optional().max(35, 'City should be less that 35 characters'),
      province: yup
        .string()
        .optional()
        .oneOf(Provinces.map(x => x.label)),
      postalCode: yup
        .string()
        .optional()
        .matches(POSTAL_CODE_PATTERN, { excludeEmptyString: true, message: 'Must match the following format: A1A1A1' })
        .max(6, 'Postal Code should be 6 characters'),
      country: yup.string().optional(),
      officePhone: yup.string().max(32, 'Must must be less than 32 characters').required('Office Phone is required'),
      cellPhone: yup.string().max(32, 'Must must be less than 32 characters').optional(),
      faxNumber: yup.string().max(32, 'Must must be less than 32 characters').optional(),
    }),
  });

export const NewsLetterSchema = yup.object().shape({
  username: yup.string().required('Name is required'),
  email: yup.string().email('Must be an email address').required('Email is required'),
  agree: yup.boolean().oneOf([true], 'Please agree before continue'),
  options: yup
    .object()
    .shape({
      lwb: yup.bool(),
      rboard: yup.bool(),
    })
    .test('options', 'Please select at least one board', function (values) {
      return values?.lwb || values?.rboard;
    }),
  recaptcha: yup.string().required('Please confirm you are not a bot').nullable(),
});

export const AddAudioComments = yup.object().shape({
  audio: yup
    .mixed()
    .required('You need to provide a file')
    .test('size', 'Max allowed file size is 10MB', value => {
      return value && value.size <= 10 * 1024 * 1024;
    })
    .test('type', 'Only mp3 files allowed', value => {
      return value && value.type.includes('audio');
    }),
  name: yup.string(),
  transcript: yup
    .mixed()
    .test('size', 'Max allowed file size is 10MB', value => {
      return value ? value.size <= 10 * 1024 * 1024 : true;
    })
    .test('type', 'Only plain text file allowed', value => {
      return value ? value.type === 'text/plain' : true;
    }),
});

export const DistributionListSchema = yup.object().shape({
  name: yup.string().required('Distribution list name is required').ensure(),
});

export const ReviewTopicSchema = yup.object().shape({
  name: yup.string().required('Topic name is required').max(255, 'Name must be less than 255 characters'),
  children: yup
    .array()
    .of(
      yup.object().shape({
        id: yup.string().optional(),
        name: yup.string().max(255, 'Name must be less than 255 characters'),
      }),
    )
    .optional(),
});

export const RedistributeReviewSchema = yup.object().shape(
  {
    userIds: yup.array().when(['sendToList', 'distributionListIds'], {
      is: (sendToList, distributionListIds) => !sendToList && !distributionListIds?.length,
      then: yup.array().required('Please select at least one user or distribution list').min(1),
    }),
    distributionListIds: yup.array().when(['sendToList', 'userIds'], {
      is: (sendToList, userIds) => !sendToList && !userIds?.length,
      then: yup.array().required('Please select at least one user or distribution list').min(1),
    }),
  },
  [
    ['userIds', 'distributionListIds'],
    ['sendToList', 'userIds'],
    ['sendToList', 'distributionListIds'],
  ],
);

export const FileUploadSchema = yup.object().shape({
  boardId: yup.string().required('A Organization is required'),
  staffIds: yup.array().required('At least one staff member is required').min(1),
  email: yup.string().email('Must be an email address').required('An email address is required'),
  projectName: yup.string().required('A project is required').max(255, 'Maximum 255 characters'),
  fileNumber: yup.string().max(255, 'Maximum 255 characters'),
  description: yup
    .string()
    .required('Document description is a required field')
    .max(1000, 'Document description must be 1000 characters or less'),
  additionalStaffNotes: yup.string().max(1000, 'Additional notes must be 1000 characters or less'),
  fileSize: yup
    .number()
    .max(
      parseInt(process.env.MAX_FTP_UPLOAD_FILE_SIZE || '5000') * 1024 * 1024,
      'Uploaded files cannot be larger than 5gb',
    ),
  file: yup.bool().oneOf([true], 'A file to upload is required'),
  recaptcha: yup.string().required('Please confirm you are not a bot').nullable(),
});

export const RequiredUserProfileSchema = yup.object().shape({
  firstName: yup.string().required('First name is required').max(100, 'First name should be less that 100 characters'),
  lastName: yup.string().required('Last name is required').max(100, 'Last name should be less that 100 characters'),
  title: yup.string().required('Title is required').max(50, 'Title should be less that 50 characters'),
  organizations: yup.array().required('Please select at least one organization').min(1),
  community: yup.string().optional().nullable(),
  contact: yup.object().shape({
    officePhone: yup.string().max(32, 'Must must be less than 32 characters').required('Office Phone is required'),
  }),
});
